import { omitBy, omit, isNull } from "lodash";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import errorHandler from "Utils/error-handler";
import { SlotSchema } from "./slot-validation";
import "./delivery-views.scss";
import { Col, Input, Row } from "antd";
import {
  ActionMenu,
  AlertBox,
  BasicModal,
  Button,
  DataTable,
  SelectInput,
} from "@life-line-lab/components-react";
import { CloseCircleOutlined, MoreOutlined } from "@ant-design/icons";
import { GenerateQRCodePDF } from "Components/shared/generate-QR-pdf";
import { TFunction } from "i18next";

interface RackViewProps {
  modal: any;
  setModal: any;
  rackData?: any;
  onUpdate: (data: any) => void;
  onUpdateStatus: (value: string, data: any, rackID: string) => void;
}

interface EditModalProps {
  modal: any;
  t: TFunction;
  input: any;
  handleFieldChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  error: any;
  handleSubmit: () => void;
  handleResetModal: () => void;
}

const EditModal: React.FC<EditModalProps> = ({
  modal,
  t,
  input,
  handleFieldChange,
  error,
  handleSubmit,
  handleResetModal,
}) => {
  const modalType = modal.type;

  return (
    <>
      <div className="modal-content-title">
        {modal?.slotData?.slotCode.toLocaleUpperCase()}
      </div>

      <div className="modal-content-text">
        <span className="bold">
          {t("label.status").toLocaleUpperCase() + ": "}
        </span>
        <span>
          {modal?.slotData?.active
            ? t("label.active").toUpperCase()
            : t("label.inactive").toUpperCase()}
        </span>
      </div>

      <div className="modal-content-text">
        <label className="inp-label inp-label">
          {modalType === "remarks"
            ? t("txt.enterRemarks")
            : t("txt.enterSlotName")}
        </label>
        <Input.TextArea
          size="middle"
          autoSize={{ minRows: 5, maxRows: 6 }}
          name={modalType}
          required={false}
          placeholder={t("txt.enterHere")}
          value={input[modalType]}
          onChange={handleFieldChange}
        />
        <AlertBox text={error[modalType]} />
      </div>

      <div className="mt-30 right modal-end-button">
        <Button
          type="primary"
          label={t("action.confirm")}
          onClick={handleSubmit}
        />
        <Button
          className="cancel-btn mr-0"
          type="default"
          label={t("action.cancel")}
          onClick={handleResetModal}
        />
      </div>
    </>
  );
};

const RackView: React.FC<RackViewProps> = ({
  modal,
  setModal,
  rackData,
  onUpdate,
  onUpdateStatus,
}) => {
  const history = useHistory();
  const { t } = useTranslation();

  const rowSelection = {
    onSelect: (row: any, selected: boolean, selectedRows) => {
      setSelectedRows(selectedRows.map((row) => row.slotCode));
    },
    onSelectAll: (selected: boolean, selectedRows: any[]) => {
      setSelectedRows(selectedRows.map((row) => row.slotCode));
    },
  };

  const handleUpdateStatus = (value, data) => {
    try {
      onUpdateStatus(
        value,
        data.remarks == "" ? omit(data, ["remarks"]) : data,
        rackData.rack._id,
      );
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);
      setError({
        ...error,
        [path]: t(`${value}`, { field: t(`label.${path}`) }),
      });
    }
  };

  const columns = [
    {
      title: t("label.no.").toLocaleUpperCase(),
      dataIndex: "index",
      render: (_: any, record: any, index: number) => <span>{index + 1}</span>,
    },
    {
      title: t("label.slotID").toLocaleUpperCase(),
      dataIndex: "slotCode",
    },
    {
      title: t("label.slotName").toLocaleUpperCase(),
      dataIndex: "name",
      render: (name: string) => <span>{name || "-"}</span>,
    },
    {
      title: t("label.availability").toLocaleUpperCase(),
      dataIndex: "status",
      render: (text: any, record: any) => (
        <span>
          {text === "OPEN"
            ? t("label.available")
            : record?.status
            ? t("label.full")
            : ""}
        </span>
      ),
    },
    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: "active",
      render: (_: any, record: any) => (
        <SelectInput
          className={`select select--${`${record?.active}`.toLowerCase()}`}
          value={`${record?.active}`}
          options={[
            {
              label: "ACTIVE",
              value: "true",
            },
            {
              label: "INACTIVE",
              value: "false",
            },
          ]}
          // onChange={value => onUpdateStatus(value as any, record, rackData.rack._id)}
          onChange={(value) => handleUpdateStatus(value, record)}
        />
      ),
    },
    {
      title: t("label.remarks").toLocaleUpperCase(),
      dataIndex: "remarks",
      render: (_: any, record: any) => <>{record?.remarks || " - "}</>,
    },
    {
      title: "",
      dataIndex: "action",
      width: 40,
      render: (_: any, record: any) => (
        <ActionMenu
          label={<MoreOutlined />}
          actions={[
            {
              label: t("txt.enterRemarks"),
              onClick: () =>
                setModal({ open: true, slotData: record, type: "remarks" }),
            },
            {
              label: t("txt.enterSlotName"),
              onClick: () =>
                setModal({ open: true, slotData: record, type: "name" }),
            },
          ]}
        />
      ),
    },
  ];

  const initialInput = {
    remarks: "",
    slotName: "",
  };
  const initialError = {};

  const [input, setInput] = useState<any>(initialInput);
  const [error, setError] = useState<any>(initialError);
  const [selectedRows, setSelectedRows] = useState<any>([]);

  useEffect(() => {
    if (modal.slotData) {
      setInput({
        ...input,
        rackID: rackData?.rack._id,
        slotCode: modal.slotData.slotCode,
        name: modal.slotData.name,
        remarks: modal.slotData.remarks,
        active: modal.slotData.active,
      });
    }
  }, [modal.slotData]);

  const handleFieldChange = (e) => {
    setError(initialError);
    setInput({ ...input, [e.target.name]: e.target.value });
  };

  const handleCloseView = () => {
    history.push(`/delivery-management/settings/assets`);
  };

  const handleResetModal = () => {
    setError(initialError);
    setModal({
      open: false,
      slotData: null,
    });
  };

  const handleSubmit = () => {
    try {
      const valInput = {
        ...input,
      };
      const value = SlotSchema.validateSync(omitBy(valInput, isNull), {
        strict: false,
        abortEarly: true,
        stripUnknown: true,
      });
      onUpdate(value);
    } catch (err) {
      console.log(err);
      const { path, value }: any = errorHandler.validation(err);
      setError({
        ...error,
        [path]: t(`${value}`, { field: t(`label.${path}`) }),
      });
    }
  };

  return (
    <div className="delivery-view">
      <DataTable
        rowKey={(record: any) => record.slotCode}
        columns={columns as any}
        rowSelection={rowSelection}
        dataSource={rackData?.rack.slots || []}
        scroll={{ x: true }}
        title={() => (
          <Row className="title-row" justify="space-between" align="middle">
            <Col className="mb-0">
              <span className="mr-20">{rackData?.rack?.name}</span>
              <span className={`status ${rackData?.rack?.active}`}>
                {rackData?.rack?.active
                  ? t("label.active").toUpperCase()
                  : t("label.inactive").toUpperCase()}
              </span>
            </Col>
            <Col className="mb-0">
              <GenerateQRCodePDF
                className="full-height mr-20"
                input={selectedRows}
              />
              <CloseCircleOutlined
                style={{ fontSize: "1rem" }}
                onClick={handleCloseView}
              />
            </Col>
          </Row>
        )}
      />
      <BasicModal
        isOpen={modal.open}
        isClosable={true}
        isMaskClosable={false}
        onClose={handleResetModal}
      >
        <EditModal
          modal={modal}
          t={t}
          input={input}
          handleFieldChange={handleFieldChange}
          error={error}
          handleSubmit={handleSubmit}
          handleResetModal={handleResetModal}
        />
      </BasicModal>
    </div>
  );
};

export default RackView;
